import { getCurrentBalance } from "@actions/balance";
import { showAlert } from "@components/alert/action";
import Loading from "@components/loading/index.jsx";
import { BettoEmptyState } from "@pages/profile-v2/betto/components/BettoEmptyState";
import React from "react";
import { Translate, getActiveLanguage } from "react-localize-redux";
import { connect } from "react-redux";

import CashbackItem from "./components/CashbackItem";
import {
	CashbackContentStyled,
	CashbackListStyled,
	CashbackRefreshButtonStyled,
	CashbackRetryButtonStyled,
	CashbackStateDescriptionStyled,
	CashbackStateTitleStyled,
	CashbackStateWrapperStyled,
	CashbackWrapperStyled,
} from "./elements";
import useController from "./useController";

const isBetto = window.TENANT === "betto";

const Cashback = ({ showAlert, getCurrentBalance, locale }) => {
	const {
		state: {
			isLoading,
			isTimeout,
			error,
			cashbacks,
			blocked,
			blockedMessage,
			isPendingBets,
			isClaiming,
			claimingId,
		},
		handlers: { startPreview, claim },
	} = useController({ showAlert, getCurrentBalance });

	const renderContent = (translate) => {
		if (isLoading) {
			return (
				<>
					<Loading loading />
					<CashbackStateWrapperStyled>
						<CashbackStateTitleStyled>
							{translate("cashback.adhoc.calculatingTitle")}
						</CashbackStateTitleStyled>
						<CashbackStateDescriptionStyled>
							{translate("cashback.adhoc.calculatingDescription")}
						</CashbackStateDescriptionStyled>
					</CashbackStateWrapperStyled>
				</>
			);
		}

		if (isTimeout) {
			return (
				<CashbackStateWrapperStyled>
					<CashbackStateTitleStyled>
						{translate("cashback.adhoc.timeoutTitle")}
					</CashbackStateTitleStyled>
					<CashbackStateDescriptionStyled>
						{translate("cashback.adhoc.timeoutDescription")}
					</CashbackStateDescriptionStyled>
					<CashbackRetryButtonStyled
						type="button"
						onClick={startPreview}>
						{translate("cashback.adhoc.tryAgain")}
					</CashbackRetryButtonStyled>
				</CashbackStateWrapperStyled>
			);
		}

		if (error) {
			const message = error?.message || translate("cashback.adhoc.errorDescription");
			return (
				<CashbackStateWrapperStyled>
					<CashbackStateTitleStyled>
						{translate("cashback.adhoc.errorTitle")}
					</CashbackStateTitleStyled>
					<CashbackStateDescriptionStyled>{message}</CashbackStateDescriptionStyled>
					<CashbackRetryButtonStyled
						type="button"
						onClick={startPreview}>
						{translate("cashback.adhoc.tryAgain")}
					</CashbackRetryButtonStyled>
				</CashbackStateWrapperStyled>
			);
		}

		if (blocked) {
			return (
				<CashbackStateWrapperStyled>
					<CashbackStateTitleStyled>
						{isPendingBets
							? translate("cashback.adhoc.pendingBetsTitle")
							: translate("cashback.adhoc.blockedTitle")}
					</CashbackStateTitleStyled>
					<CashbackStateDescriptionStyled>
						{blockedMessage ||
							translate(
								isPendingBets
									? "cashback.adhoc.pendingBetsDescription"
									: "cashback.adhoc.blockedDescription"
							)}
					</CashbackStateDescriptionStyled>
				</CashbackStateWrapperStyled>
			);
		}

		if (cashbacks.length === 0) {
			const emptyMessage = blockedMessage || translate("cashback.adhoc.emptyDescription");

			if (isBetto) {
				return (
					<BettoEmptyState title={emptyMessage}>
						<CashbackRefreshButtonStyled
							type="button"
							onClick={startPreview}>
							{translate("cashback.adhoc.refresh")}
						</CashbackRefreshButtonStyled>
					</BettoEmptyState>
				);
			}

			return (
				<CashbackStateWrapperStyled>
					<CashbackStateTitleStyled>
						{translate("cashback.adhoc.emptyTitle")}
					</CashbackStateTitleStyled>
					<CashbackStateDescriptionStyled>{emptyMessage}</CashbackStateDescriptionStyled>
					<CashbackRefreshButtonStyled
						type="button"
						onClick={startPreview}>
						{translate("cashback.adhoc.refresh")}
					</CashbackRefreshButtonStyled>
				</CashbackStateWrapperStyled>
			);
		}

		return (
			<CashbackListStyled>
				{cashbacks.map((item) => (
					<CashbackItem
						key={item.cashbackId}
						cashback={item}
						isClaiming={isClaiming && claimingId === item.cashbackId}
						isAnyClaiming={isClaiming}
						onClaim={claim}
						translate={translate}
						locale={locale}
					/>
				))}
			</CashbackListStyled>
		);
	};

	return (
		<Translate>
			{({ translate }) => (
				<CashbackWrapperStyled>
					<CashbackContentStyled>{renderContent(translate)}</CashbackContentStyled>
				</CashbackWrapperStyled>
			)}
		</Translate>
	);
};

const mapStateToProps = (state) => ({
	locale: getActiveLanguage(state.localize)?.code,
});

const mapDispatchToProps = {
	getCurrentBalance,
	showAlert,
};

export default connect(mapStateToProps, mapDispatchToProps)(Cashback);
